import type { Permission, SearchHit } from '@itc/shared';
// @itc/ui/icons, not @itc/ui: plain tsc (this package's typecheck) can't read .vue files.
import type { IconName } from '@itc/ui/icons';
import type { Component } from 'vue';
import type { RouteRecordRaw } from 'vue-router';

// A web module's registration with the shell (design §7.5). Phase 1 adds admin
// sections and notification renderers; phase 2A adds Home widgets and nav badges.

declare module 'vue-router' {
  interface RouteMeta {
    /** Who may open the page. Routes without one require a login. */
    permission?: Permission;
    /** 'bare' pages render without the shell (the login page). */
    layout?: 'shell' | 'bare';
    title?: string;
  }
}

/**
 * A live count on a nav item, such as what's waiting on the user. The shell fetches
 * `count()` and fetches it again whenever one of `topics(userId)` sends a message,
 * through the shared live client. Hidden at 0, and for users without `permission`.
 */
export interface NavBadge {
  permission: Permission;
  count: () => Promise<number>;
  /** The live topics whose messages may change the count, for this user: at most 4. */
  topics: (userId: string, can: (permission: string) => boolean) => readonly string[];
  /** What the count is, for screen readers: "3 waiting on you". */
  label: (count: number) => string;
}

export interface NavItem {
  label: string;
  to: string;
  permission: Permission;
  /** The icon over the label in the rail and the bottom bar, from @itc/ui's set. */
  icon: IconName;
  badge?: NavBadge;
}

/**
 * A panel on Home (design §8), after Sierra's Bridge. Shown to users with
 * `permission`, in module order; its component is only loaded for them.
 */
export interface ModuleWidget {
  /** `<module>.<what>`. */
  id: string;
  title: string;
  permission: Permission;
  component: () => Promise<Component | { default: Component }>;
  /** Grid columns it spans on a wide screen. Default 1. */
  span?: 1 | 2;
}

/** A module's section of the admin panel (plan 1C Assumption 13). */
export interface AdminSection {
  label: string;
  /** Under `/admin/<module>/`. */
  path: string;
  component: Component | (() => Promise<Component | { default: Component }>);
  /** Who sees and may open it. */
  permission: Permission;
}

/** What a report starts from, built in the browser with the viewer's own reads. */
export interface ReportDraft {
  title: string;
  body: string;
  /** The op it's about, kept with the saved report. */
  op?: string | null;
}

/** What a report is about, when it's about one thing: an after-action report's op. */
export interface ReportChoice {
  label: string;
  options: readonly { value: string; label: string }[];
  /** The value offered first. */
  initial: string | null;
  /** Shown when there's nothing to choose. */
  empty: string;
}

/**
 * A report type a module can draft on the Library's Reports tab (ADR 0015). Shown to
 * users with `permission`, which must cover every read `draft` makes. Load the
 * generators inside `draft`, with `import()`, as a module's web entry loads eagerly.
 */
export interface ModuleReport {
  /** One of the Library's report types, or any other: it shows as "Report". */
  type: string;
  permission: Permission;
  /** Asked for first, if the report needs one thing chosen. */
  choose?: () => Promise<ReportChoice>;
  /** `choice` is the value chosen, or the link's `op`; null for a report that asks for nothing. */
  draft: (choice: string | null) => Promise<ReportDraft>;
}

/**
 * A whole-page card the Library's Reports tab lists (ADR 0015). `spec` returns what
 * `@itc/cards` draws, with the viewer's own reads: `permission` must cover them.
 */
export interface ModuleCard {
  /** `<module>.<what>`. */
  id: string;
  label: string;
  permission: Permission;
  variants?: readonly { value: string; label: string }[];
  spec?: (variant: string | null) => Promise<{ title: string } | null>;
  /** Instead of `spec`: a button that opens the module's own sheet, as the weekly post does. */
  component?: () => Promise<Component | { default: Component }>;
}

/** A module's part of global search (ADR 0015): shown to users with any of `permissions`. Its endpoint decides what they find. */
export interface ModuleSearch {
  permissions: readonly Permission[];
  run: (query: string, signal: AbortSignal) => Promise<readonly SearchHit[]>;
}

/** Posts a drawn card. `key` is its Idempotency-Key, one per draw. */
export interface ModuleCardPost {
  permission: string;
  post: (file: File, key: string) => Promise<void>;
}

export interface WebModuleDefinition {
  name: string;
  routes: RouteRecordRaw[];
  nav: NavItem[];
  /**
   * Components that show the module's own notification kinds in the inbox, the panel
   * and toasts, by kind. Each gets the item as its `item` prop. Kinds without one use
   * the default: the title, the body and the link.
   */
  notificationRenderers?: Readonly<Record<string, Component>>;
  /** Sections of the admin panel, listed under the module's `title`. */
  admin?: readonly AdminSection[];
  /** How the admin panel names the module. Defaults to its name. */
  title?: string;
  /** Panels on Home. */
  widgets?: readonly ModuleWidget[];
  /** Report types for the Library's Reports tab, which reads them through `useModules()`. */
  reports?: readonly ModuleReport[];
  /** Cards for the Library's Reports tab. */
  cards?: readonly ModuleCard[];
  /** A module's part of global search. */
  search?: ModuleSearch;
  /** What cards say about the org. The Library alone provides it, and it may fail: `@itc/cards` falls back. */
  orgInfo?: () => Promise<{ cardFooter: string }>;
  /** How a Share image posts to Discord. The Library alone provides it: if it's off, or the viewer lacks `permission`, there's no button. */
  cardPost?: ModuleCardPost;
}

export interface WebModule extends Readonly<WebModuleDefinition> {
  readonly kind: 'web-module';
}

export function defineWebModule(definition: WebModuleDefinition): WebModule {
  const { name } = definition;
  if (!/^[a-z][a-z0-9]*$/.test(name)) throw new Error(`Web module name "${name}" must be lower-case letters and digits`);
  for (const route of definition.routes) {
    if (route.path !== `/${name}` && !route.path.startsWith(`/${name}/`)) {
      throw new Error(`Web module "${name}": route ${route.path} must be under /${name}`);
    }
    if (route.meta?.permission === undefined) {
      throw new Error(`Web module "${name}": route ${route.path} must declare meta.permission`);
    }
  }
  for (const section of definition.admin ?? []) {
    if (section.path !== `/admin/${name}` && !section.path.startsWith(`/admin/${name}/`)) {
      throw new Error(`Web module "${name}": admin section ${section.path} must be under /admin/${name}/`);
    }
    if (section.permission === 'public') throw new Error(`Web module "${name}": admin section ${section.path} can't be public`);
  }
  const widgetIds = new Set<string>();
  for (const widget of definition.widgets ?? []) {
    if (!widget.id.startsWith(`${name}.`) || !/^[a-z][a-z0-9]*\.[a-z0-9_-]+$/.test(widget.id)) throw new Error(`Web module "${name}": widget id "${widget.id}" must be ${name}.<what> in lower case`);
    if (widgetIds.has(widget.id)) throw new Error(`Web module "${name}": widget "${widget.id}" is declared twice`);
    widgetIds.add(widget.id);
    if (widget.permission === 'public') throw new Error(`Web module "${name}": widget ${widget.id} can't be public`);
  }
  for (const item of definition.nav) {
    if (item.badge?.permission === 'public') throw new Error(`Web module "${name}": the badge on ${item.to} can't be public`);
  }
  const reportTypes = new Set<string>();
  for (const report of definition.reports ?? []) {
    if (reportTypes.has(report.type)) throw new Error(`Web module "${name}": report type "${report.type}" is declared twice`);
    reportTypes.add(report.type);
    if (report.permission === 'public') throw new Error(`Web module "${name}": report type ${report.type} can't be public`);
  }
  const cardIds = new Set<string>();
  for (const card of definition.cards ?? []) {
    if (!card.id.startsWith(`${name}.`)) throw new Error(`Web module "${name}": card id "${card.id}" must be ${name}.<what>`);
    if (cardIds.has(card.id)) throw new Error(`Web module "${name}": card "${card.id}" is declared twice`);
    cardIds.add(card.id);
    if (card.permission === 'public') throw new Error(`Web module "${name}": card ${card.id} can't be public`);
  }
  if (definition.cardPost !== undefined && !definition.cardPost.permission.startsWith(`${name}.`)) throw new Error(`Web module "${name}": cardPost needs one of its own permissions (${name}.*)`);
  if (definition.search?.permissions.includes('public') === true) throw new Error(`Web module "${name}": search can't be public`);
  for (const kind of Object.keys(definition.notificationRenderers ?? {})) {
    if (!kind.startsWith(`${name}.`)) throw new Error(`Web module "${name}": a notification renderer for "${kind}" must be for one of its own kinds (${name}.*)`);
  }
  return Object.freeze({ ...definition, kind: 'web-module' as const });
}

/** The renderer a module registered for a notification kind, or null for the default. */
export function notificationRenderer(modules: readonly WebModule[], kind: string): Component | null {
  for (const module of modules) {
    const renderer = module.notificationRenderers?.[kind];
    if (renderer !== undefined) return renderer;
  }
  return null;
}

/** Every module's widgets that a user may see, in module order. Filter before loading any. */
export function visibleWidgets(modules: readonly WebModule[], can: (permission: Permission) => boolean): ModuleWidget[] {
  return modules.flatMap((module) => (module.widgets ?? []).filter((widget) => can(widget.permission)));
}

/** Every module's report types that a user may draft, in module order. */
export function visibleReports(modules: readonly WebModule[], can: (permission: Permission) => boolean): ModuleReport[] {
  return modules.flatMap((module) => (module.reports ?? []).filter((report) => can(report.permission)));
}

/** Every module's cards that a user may draw, in module order. */
export function visibleCards(modules: readonly WebModule[], can: (permission: Permission) => boolean): ModuleCard[] {
  return modules.flatMap((module) => (module.cards ?? []).filter((card) => can(card.permission)));
}

/** The modules a user may search, by name. */
export function visibleSearches(modules: readonly WebModule[], can: (permission: Permission) => boolean): { module: string; title: string; search: ModuleSearch }[] {
  return modules.flatMap((module) => (module.search?.permissions.some(can) === true ? [{ module: module.name, title: module.title ?? module.name, search: module.search }] : []));
}

/** The module whose routes contain `path`, if any. */
export function moduleForPath(modules: readonly WebModule[], path: string): WebModule | undefined {
  return modules.find((module) => path === `/${module.name}` || path.startsWith(`/${module.name}/`));
}
